import { useMemo } from 'react'

import type { Account } from '@b-stock/account-api-client'
import { OnlinePaymentBlocked } from '@b-stock/account-api-client'

import useAccount from '@/buyer-portal/helpers/useAccount'

type OnlinePaymentBlockedByMethod = Partial<Record<string, boolean>>

type OnlinePaymentBlockedState =
  | Account['onlinePaymentBlocked']
  | OnlinePaymentBlockedByMethod

type BuyerFacingAchAvailability = 'available' | 'not-available-yet'

type BuyerFacingAchAvailabilityState = {
  achAvailability: BuyerFacingAchAvailability
  isACHBlocked: boolean
  isWireFallbackAvailable: boolean
}

const isOnlinePaymentBlocked = (
  onlinePaymentBlocked: OnlinePaymentBlockedState | undefined,
  method: OnlinePaymentBlocked
) => {
  if (Array.isArray(onlinePaymentBlocked)) {
    return onlinePaymentBlocked.includes(method)
  }

  return onlinePaymentBlocked?.[method] === true
}

export const selectBuyerFacingAchAvailability = ({
  onlinePaymentBlocked,
}: {
  onlinePaymentBlocked?: OnlinePaymentBlockedState
}): BuyerFacingAchAvailabilityState => {
  const isACHBlocked = isOnlinePaymentBlocked(
    onlinePaymentBlocked,
    OnlinePaymentBlocked.Ach
  )

  return {
    achAvailability: isACHBlocked ? 'not-available-yet' : 'available',
    isACHBlocked,
    isWireFallbackAvailable: isACHBlocked,
  }
}

export const selectPaymentRestrictions = (
  onlinePaymentBlocked?: OnlinePaymentBlockedState
) => {
  return {
    isACHBlocked: isOnlinePaymentBlocked(
      onlinePaymentBlocked,
      OnlinePaymentBlocked.Ach
    ),
    isCreditCardBlocked: isOnlinePaymentBlocked(
      onlinePaymentBlocked,
      OnlinePaymentBlocked.CreditCard
    ),
    isNetTermsBlocked: isOnlinePaymentBlocked(
      onlinePaymentBlocked,
      OnlinePaymentBlocked.NetTerms
    ),
  }
}

export type PaymentRestrictions = ReturnType<typeof selectPaymentRestrictions>

export type UsePaymentRestrictionsResult = PaymentRestrictions & {
  isWireFallbackAvailable?: boolean
  isAccountLoading?: boolean
}

export const usePaymentRestrictions = (): UsePaymentRestrictionsResult => {
  const account = useAccount()
  return useMemo(() => {
    const restrictions = selectPaymentRestrictions(
      account.data?.onlinePaymentBlocked
    )

    return {
      ...restrictions,
      isAccountLoading: account.isLoading || account.isFetching,
      // Account-level wire restrictions are not supported yet (PS-10757).
      isWireFallbackAvailable: restrictions.isACHBlocked,
    }
  }, [account.data, account.isFetching, account.isLoading])
}

export const useBuyerFacingAchAvailability = () => {
  const account = useAccount()
  return useMemo(
    () =>
      selectBuyerFacingAchAvailability({
        onlinePaymentBlocked: account.data?.onlinePaymentBlocked,
      }),
    [account.data?.onlinePaymentBlocked]
  )
}
